import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import { noop } from "../../utils";

class BaselineHandle extends Component {
  constructor(props) {
    super(props);

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  isHover(moreProps) {
    const { separatorPrice, enabled } = this.props;

    if (!enabled || separatorPrice == null) return false;

    const { chartConfig, mouseXY } = moreProps;

    if (!chartConfig || !chartConfig.yScale || !chartConfig.width || !mouseXY) {
      return false;
    }

    const { yScale, width } = chartConfig;
    const [mouseX, mouseY] = mouseXY;

    const separatorY = yScale(separatorPrice);
    const handleX = width - 70; // Match the drawOnCanvas position
    const handleWidth = 32; // Match the drawOnCanvas size
    const handleHeight = 16; // Match the drawOnCanvas size

    // Check if mouse is over the handle area
    return (
      mouseX >= handleX &&
      mouseX <= handleX + handleWidth &&
      mouseY >= separatorY - handleHeight / 2 &&
      mouseY <= separatorY + handleHeight / 2
    );
  }

  drawOnCanvas(ctx, moreProps) {
    const { separatorPrice, enabled, hovering } = this.props;

    if (!enabled || separatorPrice == null) return;

    const { chartConfig } = moreProps;

    if (!chartConfig || !chartConfig.yScale || !chartConfig.width) {
      return;
    }

    const { yScale, width } = chartConfig;

    const separatorY = yScale(separatorPrice);
    const handleX = width - 70; // More space from edge
    const handleWidth = 32; // Slightly smaller
    const handleHeight = 16; // More compact
    const handleY = separatorY - handleHeight / 2;

    // Professional color scheme
    const bgColor = hovering ? "#F8F9FA" : "#FFFFFF";
    const borderColor = hovering ? "#6C757D" : "#ADB5BD";
    const iconColor = hovering ? "#495057" : "#6C757D";

    // Subtle shadow effect
    if (!hovering) {
      ctx.fillStyle = "rgba(0, 0, 0, 0.08)";
      ctx.beginPath();
      ctx.roundRect(handleX + 1, handleY + 1, handleWidth, handleHeight, 6);
      ctx.fill();
    }

    // Main handle background
    ctx.fillStyle = bgColor;
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = hovering ? 1.5 : 1;
    ctx.globalAlpha = hovering ? 1.0 : 0.9;

    // Rounded rectangle with subtle radius
    const radius = 6;
    ctx.beginPath();
    ctx.roundRect(handleX, handleY, handleWidth, handleHeight, radius);
    ctx.fill();
    ctx.stroke();

    // Draw resize icon (⋮⋮) instead of arrows
    ctx.fillStyle = iconColor;
    ctx.globalAlpha = hovering ? 0.9 : 0.7;

    const dotSize = 1.5;
    const centerX = handleX + handleWidth / 2;
    const centerY = separatorY;

    // Left column of dots
    ctx.beginPath();
    ctx.arc(centerX - 3, centerY - 4, dotSize, 0, 2 * Math.PI);
    ctx.arc(centerX - 3, centerY, dotSize, 0, 2 * Math.PI);
    ctx.arc(centerX - 3, centerY + 4, dotSize, 0, 2 * Math.PI);
    ctx.fill();

    // Right column of dots
    ctx.beginPath();
    ctx.arc(centerX + 3, centerY - 4, dotSize, 0, 2 * Math.PI);
    ctx.arc(centerX + 3, centerY, dotSize, 0, 2 * Math.PI);
    ctx.arc(centerX + 3, centerY + 4, dotSize, 0, 2 * Math.PI);
    ctx.fill();

    // Reset context
    ctx.globalAlpha = 1.0;
  }

  renderSVG() {
    throw new Error("svg not implemented for BaselineHandle");
  }

  render() {
    const { enabled, interactiveCursorClass } = this.props;
    const { onHover, onUnHover } = this.props;
    const { onDragStart, onDrag, onDragComplete } = this.props;

    if (!enabled) return null;

    return (
      <GenericChartComponent
        clip={false}
        isHover={this.isHover}
        svgDraw={this.renderSVG}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        enableDragOnHover
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={["mousemove", "pan", "drag"]}
      />
    );
  }
}

BaselineHandle.propTypes = {
  separatorPrice: PropTypes.number,
  enabled: PropTypes.bool,
  hovering: PropTypes.bool,
  selected: PropTypes.bool,
  handleFill: PropTypes.string,
  handleStroke: PropTypes.string,
  textFill: PropTypes.string,
  fontSize: PropTypes.number,
  fontFamily: PropTypes.string,
  interactiveCursorClass: PropTypes.string,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
  onDragStart: PropTypes.func,
  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
};

BaselineHandle.defaultProps = {
  enabled: true,
  hovering: false,
  selected: false,
  handleFill: "#FFFFFF",
  handleStroke: "#607D8B",
  textFill: "#333333",
  fontSize: 12,
  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
  interactiveCursorClass: "react-stockcharts-ns-resize-cursor",
  onHover: noop,
  onUnHover: noop,
  onDragStart: noop,
  onDrag: noop,
  onDragComplete: noop,
};

export default BaselineHandle;
